// src/pages/AdminPayments.js
import { useState, useEffect, useCallback } from 'react';
import { CheckCircle, XCircle, Search, DollarSign, Clock, TrendingUp } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { admin } from '../services/api';

function Badge({ status }) {
  const styles = {
    pending:   { bg: '#fef3c7', color: '#92400e' },
    confirmed: { bg: '#dcfce7', color: '#166534' },
    cancelled: { bg: '#f1f5f9', color: '#64748b' },
  };
  const s = styles[status] || styles.cancelled;
  return (
    <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold"
      style={{ backgroundColor: s.bg, color: s.color }}>
      {status}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  const theme = useTheme();
  return (
    <div className="rounded-2xl p-4" style={{ backgroundColor: theme.card, boxShadow: theme.isDark ? '0 4px 24px rgba(0,0,0,0.35)' : '0 2px 16px rgba(0,0,0,0.08)' }}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} style={{ color }} />
        <p className="text-[10px] font-medium uppercase tracking-wider" style={{ color: theme.textMuted }}>{label}</p>
      </div>
      <p className="text-xl font-bold" style={{ color }}>{value}</p>
    </div>
  );
}

export default function AdminPayments() {
  const theme  = useTheme();
  const [payments,  setPayments]  = useState([]);
  const [stats,     setStats]     = useState({});
  const [filter,    setFilter]    = useState('pending');
  const [search,    setSearch]    = useState('');
  const [loading,   setLoading]   = useState(true);
  const [acting,    setActing]    = useState(null);
  const [meta,      setMeta]      = useState({ page: 1, total: 0, pages: 1 });

  const load = useCallback(async (page = 1) => {
    setLoading(true);
    const params = { status: filter, page };
    if (search) params.search = search;
    const res = await admin.listPayments(params);
    setLoading(false);
    if (res.success) {
      setPayments(res.data.data || []);
      setStats(res.data.stats  || {});
      setMeta(res.data.meta    || {});
    }
  }, [filter, search]);

  useEffect(() => { load(1); }, [load]);

  async function handleAction(id, action) {
    if (!window.confirm(`${action === 'confirm' ? 'Confirm' : 'Cancel'} this payment?`)) return;
    setActing(id);
    const fn = action === 'confirm' ? admin.confirmPayment : admin.cancelPayment;
    await fn(id);
    setActing(null);
    load(meta.page);
  }

  const inputStyle = { backgroundColor: theme.background, borderColor: theme.border, color: theme.text };

  return (
    <div className="space-y-4 pb-10">
      <h1 className="text-xl font-bold" style={{ color: theme.text }}>Payments</h1>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <StatCard icon={Clock}       label="Pending"   value={stats.pending   || 0} color="#f59e0b" />
        <StatCard icon={CheckCircle} label="Confirmed" value={stats.confirmed || 0} color="#16a34a" />
        <StatCard icon={TrendingUp}  label="Revenue"   value={`R${Number(stats.revenue || 0).toLocaleString('en-ZA')}`} color="#00ACC1" />
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        <div className="flex rounded-xl overflow-hidden border" style={{ borderColor: theme.border }}>
          {['pending','confirmed','cancelled'].map(s => (
            <button key={s} onClick={() => setFilter(s)}
              className="px-3 py-2 text-xs font-medium capitalize transition-colors"
              style={{ backgroundColor: filter === s ? '#00ACC1' : theme.card, color: filter === s ? '#fff' : theme.textSecondary }}>
              {s}
            </button>
          ))}
        </div>
        <form onSubmit={e => { e.preventDefault(); load(1); }} className="flex gap-2 flex-1 min-w-[180px]">
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search..."
            className="flex-1 px-3 py-2 rounded-xl border text-sm outline-none"
            style={inputStyle} />
          <button type="submit" className="p-2 rounded-xl" style={{ backgroundColor: theme.card }}>
            <Search size={15} style={{ color: theme.textMuted }} />
          </button>
        </form>
      </div>

      {/* Loading */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="w-6 h-6 rounded-full border-2 border-transparent animate-spin" style={{ borderTopColor: '#00ACC1' }} />
        </div>
      ) : payments.length === 0 ? (
        <div className="rounded-2xl p-8 text-center text-sm" style={{ backgroundColor: theme.card, color: theme.textMuted }}>
          No payments found
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block rounded-2xl overflow-hidden" style={{ backgroundColor: theme.card, boxShadow: theme.isDark ? '0 4px 24px rgba(0,0,0,0.35)' : '0 2px 16px rgba(0,0,0,0.08)' }}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                    {['Reference','Email','Plan','Amount','Status','Date','Actions'].map(h => (
                      <th key={h} className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: theme.textMuted }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {payments.map(p => (
                    <tr key={p.id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                      <td className="px-5 py-3 font-mono font-bold text-xs" style={{ color: '#00ACC1' }}>{p.reference}</td>
                      <td className="px-5 py-3 text-xs" style={{ color: theme.text }}>{p.email}</td>
                      <td className="px-5 py-3 capitalize text-xs" style={{ color: theme.textSecondary }}>{p.plan}</td>
                      <td className="px-5 py-3 font-semibold text-sm" style={{ color: theme.text }}>R{p.amount}</td>
                      <td className="px-5 py-3"><Badge status={p.status} /></td>
                      <td className="px-5 py-3 text-xs" style={{ color: theme.textMuted }}>
                        {new Date(p.created_at).toLocaleDateString('en-ZA')}
                      </td>
                      <td className="px-5 py-3">
                        {p.status === 'pending' && (
                          <div className="flex gap-2">
                            <button onClick={() => handleAction(p.id, 'confirm')} disabled={acting === p.id}
                              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-50"
                              style={{ backgroundColor: '#16a34a' }}>
                              <CheckCircle size={12} />Confirm
                            </button>
                            <button onClick={() => handleAction(p.id, 'cancel')} disabled={acting === p.id}
                              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-50"
                              style={{ backgroundColor: theme.border, color: theme.textSecondary }}>
                              <XCircle size={12} />Cancel
                            </button>
                          </div>
                        )}
                        {p.voucher_code && <span className="font-mono text-xs" style={{ color: theme.textMuted }}>{p.voucher_code}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {payments.map(p => (
              <div key={p.id} className="rounded-2xl p-4 space-y-3"
                style={{ backgroundColor: theme.card, boxShadow: theme.isDark ? '0 4px 24px rgba(0,0,0,0.35)' : '0 2px 16px rgba(0,0,0,0.08)' }}>
                <div className="flex items-start justify-between">
                  <span className="font-mono text-sm font-bold" style={{ color: '#00ACC1' }}>{p.reference}</span>
                  <Badge status={p.status} />
                </div>
                <p className="text-sm" style={{ color: theme.text }}>{p.email}</p>
                <div className="flex items-center justify-between text-xs" style={{ color: theme.textMuted }}>
                  <span className="capitalize">{p.plan}</span>
                  <span className="font-semibold text-sm" style={{ color: theme.text }}>R{p.amount}</span>
                  <span>{new Date(p.created_at).toLocaleDateString('en-ZA')}</span>
                </div>
                {p.status === 'pending' && (
                  <div className="flex gap-2 pt-1">
                    <button onClick={() => handleAction(p.id, 'confirm')} disabled={acting === p.id}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-semibold text-white disabled:opacity-50"
                      style={{ backgroundColor: '#16a34a' }}>
                      <CheckCircle size={13} />Confirm
                    </button>
                    <button onClick={() => handleAction(p.id, 'cancel')} disabled={acting === p.id}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-semibold disabled:opacity-50"
                      style={{ backgroundColor: theme.border, color: theme.textSecondary }}>
                      <XCircle size={13} />Cancel
                    </button>
                  </div>
                )}
                {p.voucher_code && (
                  <p className="font-mono text-xs pt-1" style={{ color: theme.textMuted }}>Voucher: {p.voucher_code}</p>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Pagination */}
      {meta.pages > 1 && (
        <div className="flex items-center gap-2 justify-center">
          <button onClick={() => load(meta.page - 1)} disabled={meta.page <= 1}
            className="px-4 py-2 rounded-xl text-xs font-medium border disabled:opacity-40"
            style={{ backgroundColor: theme.card, borderColor: theme.border, color: theme.text }}>Prev</button>
          <span className="text-xs" style={{ color: theme.textMuted }}>{meta.page} / {meta.pages}</span>
          <button onClick={() => load(meta.page + 1)} disabled={meta.page >= meta.pages}
            className="px-4 py-2 rounded-xl text-xs font-medium border disabled:opacity-40"
            style={{ backgroundColor: theme.card, borderColor: theme.border, color: theme.text }}>Next</button>
        </div>
      )}
    </div>
  );
}